import { noop } from '@accedo/general-utils';
import { useState, useRef, useLayoutEffect, useEffect } from 'preact/hooks';
import { RefObject } from 'preact';
import { KeyCodes } from '@accedo/virtual-key';
import { Messenger } from './Messenger';
import KeyEventHelper from './KeyEventHelper';

export const useReferredState = <T>(
  initialValue: T,
): [T, { current: T }, (value: T) => void] => {
  const [state, setState] = useState<T>(initialValue);
  const reference = useRef<T>(state);

  const setReferredState = (value: T): void => {
    reference.current = value;
    setState(value);
  };

  return [state, reference, setReferredState];
};

export const useInterval = (
  callback: () => void,
  delay: number | null,
): void => {
  const savedCallback = useRef(callback);

  // Remember the latest callback if it changes.
  useLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return noop;
    }

    const id = setInterval(() => savedCallback.current(), delay);

    return () => clearInterval(id);
  }, [delay]);
};

export const useFirstTruthyValue = <T>(
  callback: () => () => void,
  dependency: T,
  condition: (dependency: T) => boolean,
): void => {
  const alreadyCalled = useRef<boolean>(false);

  useEffect((): (() => void) => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    let unlisten = (): void => {};

    if (alreadyCalled.current) return unlisten;

    if (condition(dependency)) {
      unlisten = callback();
      alreadyCalled.current = true;
    }

    return (): void => {
      if (unlisten) {
        unlisten();
      }
    };
  }, [dependency]);
};

export const useMountedRef = (): RefObject<boolean> => {
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  });

  return mounted;
};

export const useMessenger = <T>(messenger: Messenger<T>): T => {
  const [state, stateRef, setState] = useReferredState<T>(
    messenger.getCurrentValue(),
  );

  useEffect(() => {
    const receiverObj = messenger.addReceiver(
      (newVal) => newVal !== stateRef.current && setState(newVal),
    );
    return () => {
      messenger.removeReceiver(receiverObj.receiver);
    };
  }, []);

  return state;
};

export const useMessengerAction = <T>(
  messenger: Messenger<T>,
  onMessengerAction: (value: T) => void,
  dependencyArray: any[] = [],
): void => {
  useEffect(() => {
    const receiverObj = messenger.addReceiver((newVal) => {
      onMessengerAction(newVal);
    });
    return () => {
      messenger.removeReceiver(receiverObj.receiver);
    };
  }, dependencyArray);
};

interface ObserverOptions {
  root?: Element | Document | null;
  rootMargin?: string;
  threshold?: number | number[];
}

export const useObserver = (
  ref: RefObject<HTMLElement>,
  options?: ObserverOptions,
  once = false,
): boolean => {
  const [isShowing, setIsShowing] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      const { intersectionRatio } = entry;

      const isIntersecting =
        typeof entry.isIntersecting === 'boolean'
          ? entry.isIntersecting
          : intersectionRatio > 0;

      setIsShowing((prevIsIntersecting) => {
        if (once && prevIsIntersecting) {
          return prevIsIntersecting;
        }

        if (prevIsIntersecting !== isIntersecting) {
          return isIntersecting;
        }

        return prevIsIntersecting;
      });
    }, options);
    observer.observe(ref.current as Element);

    return () => observer.disconnect();
  }, []);

  return isShowing;
};

type KeyName = keyof typeof KeyCodes.vKey;

export const useKeyPress = (
  targetKeyName: KeyName,
  onKeyPress: () => boolean,
  isEnabled = true,
): void => {
  useEffect(() => {
    if (!isEnabled) {
      return () => undefined;
    }

    const eventListenerId = `${Date.now()}-${targetKeyName}`;
    KeyEventHelper.addKeydownListener((keyName) => {
      if (KeyCodes.vKey[targetKeyName] === keyName) {
        return Boolean(onKeyPress?.());
      }

      return false;
    }, eventListenerId);

    return () => KeyEventHelper.removeKeydownListener(eventListenerId);
  }, []);
};

export const useAsyncValue = <T>(
  promiseFunc: () => Promise<T | undefined>,
): RefObject<T | undefined> => {
  const promiseRef = useRef<T>();

  useEffect(() => {
    promiseFunc()
      .then((value) => (promiseRef.current = value))
      .catch(() => (promiseRef.current = undefined));
  }, []);

  return promiseRef;
};
